import type { PillTone } from "@/components/ui/pill";
import type { PlanAdherenceBounds } from "@/hooks/useOrganizationPolicy";

// Spec 079 (T052/FR-030/FR-036) — as FAIXAS da aderência vêm da organização.
//
// `IN_PROGRESS_LOW` e `IN_PROGRESS_HIGH` eram constantes exportadas (95 e 105). Duas coisas
// mudaram, e a segunda é a que mais enganava:
//   1. Os dois números entram por ARGUMENTO — quem os obtém é `usePlanAdherenceBounds()`.
//   2. As DESCRIÇÕES das faixas eram literais de texto: `"< 95%"` e `"95% – 105%"` escritos à mão
//      no `PLAN_ADHERENCE_BANDS`. Uma fábrica que mudasse o piso para 90 passaria a ver a tela
//      EXPLICANDO faixas que ela não aplica mais — a legenda mentindo sobre o próprio veredito
//      exibido ao lado. Agora as descrições são DERIVADAS dos valores vigentes.
//
// `COMPLETED_TARGET` permanece constante: 100% do plano é a definição de "plano cumprido", não uma
// preferência da fábrica. Uma meta configurável aqui significaria declarar cumprido um plano que
// não foi cumprido.
export const COMPLETED_TARGET = 100;

export type PlanAdherenceVerdictId =
  | "atrasado"
  | "no-plano"
  | "adiantado"
  | "plano-cumprido"
  | "acima-do-plano";

export interface PlanAdherenceVerdict {
  id: PlanAdherenceVerdictId;
  label: string;
  tone: PillTone;
}

export function derivePlanAdherenceVerdict(
  basis: "in-progress" | "completed" | null,
  value: number,
  overrunPct: number | null,
  bounds: PlanAdherenceBounds
): PlanAdherenceVerdict {
  if (basis === "completed") {
    return overrunPct && overrunPct > 0
      ? { id: "acima-do-plano", label: `+${overrunPct.toFixed(0)}% além do plano`, tone: "info" }
      : { id: "plano-cumprido", label: "plano cumprido", tone: "success" };
  }
  if (value > bounds.high) return { id: "adiantado", label: "adiantado", tone: "success" };
  if (value < bounds.low) return { id: "atrasado", label: "atrasado", tone: "danger" };
  return { id: "no-plano", label: "no plano", tone: "success" };
}

export interface PlanAdherenceBand {
  id: PlanAdherenceVerdictId;
  label: string;
  range: string;
  tone: PillTone;
  description: string;
  appliesTo: "in-progress" | "completed";
}

/**
 * As faixas EXPLICADAS na tela, derivadas dos limiares vigentes (FR-036).
 *
 * ⚠️ Função, e não constante: os números aparecem no `range` E na `description`, e escrevê-los à
 * mão era o defeito. Uma fábrica com piso 90 lia "< 95%" ao lado de um veredito calculado contra
 * 90 — a legenda contradizendo o crachá, na tela cujo propósito é explicar o crachá.
 */
export function planAdherenceBands(bounds: PlanAdherenceBounds): PlanAdherenceBand[] {
  const low = fmt(bounds.low);
  const high = fmt(bounds.high);
  return [
    {
      id: "atrasado",
      label: "atrasado",
      range: `< ${low}%`,
      tone: "danger",
      appliesTo: "in-progress",
      description: `Ritmo abaixo de ${low}% do planejado para a fração de tempo já decorrida.`,
    },
    {
      id: "no-plano",
      label: "no plano",
      range: `${low}% – ${high}%`,
      tone: "success",
      appliesTo: "in-progress",
      description: `Produção acompanha o ritmo planejado dentro da margem de ${low}% a ${high}%.`,
    },
    {
      id: "adiantado",
      label: "adiantado",
      range: `> ${high}%`,
      tone: "success",
      appliesTo: "in-progress",
      description: `Produção acima de ${high}% do ritmo planejado para o tempo decorrido.`,
    },
    {
      id: "plano-cumprido",
      label: "plano cumprido",
      range: `≥ ${COMPLETED_TARGET}%`,
      tone: "success",
      appliesTo: "completed",
      description: "A quantidade planejada foi atingida (ou superada) ao final da produção.",
    },
    {
      id: "acima-do-plano",
      label: "produzido acima do plano",
      range: `> ${COMPLETED_TARGET}%`,
      tone: "info",
      appliesTo: "completed",
      description:
        "Produziu além do planejado; o excedente é informado como sinal, sem cap em 100%.",
    },
  ];
}

/** Sem casa decimal quando o limiar é inteiro — "95%" e não "95,0%". */
function fmt(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1).replace(".", ",");
}
